import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Redirect } from 'react-router-dom';

import { Wizard } from 'gw-portals-wizard-react';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';
import { withViewModelService } from 'gw-portals-viewmodel-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { ErrorBoundary } from 'gw-portals-error-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { ModalNextProvider, Loader } from '@jutro/components';
import { MockUpUtil } from 'gw-portals-util-js';
import messages from './PEFARMWizard.messages';

import wizardConfig from './config/farm-wizard-config.json5';
import wizardStepToFieldMapping from './config/farm-wizard-step-to-field-mapping.json5';
import styles from './PEFARMWizard.module.scss';

const PATH_TO_MOCK_DATA = 'quote.farm';
const MOCK_DATA_TO_SET = ['bindData.contactPhone'];
const MOCK_DATA_TO_REMOVE = ['bindData.contactPhone'];

function PEFARMWizard(props) {
    const { steps, title } = wizardConfig;
    const { authHeader } = useAuthentication();
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { viewModelService, history, location } = props;

    useEffect(
        () => {
            const viewModelContext = {
                AccountEmailRequired: false,
                DriverEmailRequired: true,
                AccountDOBRequired: false
            };
            if (!location.state) {
                history.push('/');
                return;
            }

            const { quoteentry } = location.state;
            const { postalCode, quoteID } = quoteentry;
            const requestData = {
                quoteID: quoteID,
                postalCode: postalCode
            };
            LoadSaveService.retrieveSubmission(requestData, authHeader)
                .then((response) => {
                    return MockUpUtil.cleanUpMockedProperties(
                        response,
                        PATH_TO_MOCK_DATA,
                        ...MOCK_DATA_TO_REMOVE
                    );
                })
                .then((response) => {
                    ClausesUtil.getDependentClausesFromServer(
                        ClauseService,
                        _.get(response, 'quoteID'),
                        authHeader
                    );
                    const submission = viewModelService.create(
                        response,
                        'pc',
                        'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                        viewModelContext
                    );
                    setInitialSubmission(submission);
                    setIsLoading(false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleCancel = useCallback(
        (cancelProps) => {
            const skipModal = ['logout', 'summary'];
            const {
                wizardSnapshot,
                param: nextPath = '',
                currentStepIndex,
                wizardData
            } = cancelProps;
            const lastPathName = nextPath.replace(/\/?.*\//g, '');
            if (
                currentStepIndex === steps.length - 1
                || skipModal.includes(lastPathName)
                || hasErrorOccurred
            ) {
                return true;
            }
            return ModalNextProvider.showConfirm({
                title: commonMessages.wantToCancel,
                message: commonMessages.infoWillBeSavedAsDraft,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.no
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                const isStatusQuoted = _.get(wizardSnapshot, 'baseData.periodStatus.value.code') === 'Quoted';

                let serverCall = LoadSaveService.updateDraftSubmission;

                if (isStatusQuoted) {
                    serverCall = LoadSaveService.updateQuotedSubmission;
                }

                const isValid = wizardData.lobData.aspects.valid
                    && wizardData.lobData.aspects.subtreeValid;
                const payload = isValid
                    ? _.get(wizardData, 'value')
                    : _.get(wizardSnapshot, 'value');
                const payloadBeforeSave = _.omit(payload, 'bindData.paymentDetails');
                const dataToSave = MockUpUtil.setMockData(
                    payloadBeforeSave,
                    PATH_TO_MOCK_DATA,
                    ...MOCK_DATA_TO_SET
                );

                setIsLoading(true);

                const redirectPath = `/quotes/${dataToSave.quoteID}/summary`;
                serverCall(dataToSave, authHeader)
                    .then(() => {
                        history.push(nextPath || redirectPath);
                    })
                    .catch(() => {
                        ModalNextProvider.showAlert({
                            title: messages.anErrorOccurredTitle,
                            message: messages.anErrorOccurred,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).catch(_.noop);
                        history.push(nextPath || redirectPath);
                    });
                return true;
            }, _.noop);
        },
        [authHeader, hasErrorOccurred, history, steps.length]
    );

    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const quoteIDFromWizard = _.get(error, 'gwInfo.params[0].quoteID');
            const quoteIDFromLocation = _.get(location, 'state.quoteentry.quoteID');
            const quoteID = quoteIDFromWizard || quoteIDFromLocation;
            const redirectPath = `/quotes/${quoteID}/summary`;
            const state = {
                pathname: redirectPath,
                state: error
            };

            return ModalNextProvider.showAlert({
                title: isQuotePage ? messages.saveQuoteError : messages.anErrorOccurredTitle,
                message: isQuotePage ? messages.saveQuoteErrorMessage : messages.anErrorOccurred,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                setHasErrorOccurred(true);
                return <Redirect to={state} />;
            }, _.noop);
        },
        [location]
    );

    const handleKnockout = useCallback(({ knockOutErrors }) => {
        history.push('/knockoutpage', { underwritingIssues: knockOutErrors });
    }, [history]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <div className={styles.PEFARMWizardContainer}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    onKnockOut={handleKnockout}
                    initialData={initialSubmission}
                    onCancel={handleCancel}
                    skipCompletedSteps
                    wizardStepToFieldMapping={wizardStepToFieldMapping}
                    onPreviousModalProps={{
                        title: commonMessages.wantToJump,
                        message: commonMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.yesModel,
                        cancelButtonText: commonMessages.cancelModel
                    }}
                />
            </ErrorBoundary>
        </div>
    );
}

PEFARMWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            quoteentry: PropTypes.shape({
                postalCode: PropTypes.string,
                quoteID: PropTypes.string
            })
        })
    }).isRequired
};

export default withViewModelService(PEFARMWizard);
